import { useState, useEffect } from "react";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dumbbell, FolderOpen, Tag, Users, TrendingUp, Target } from "lucide-react";

export default function WorkoutOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOverview();
  }, []);

  const fetchOverview = async () => {
    try {
      const res = await api.get("/workouts/overview");
      setData(res.data);
    } catch (err) {
      console.error("Failed to fetch overview:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
          <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider">Loading</p>
        </div>
      </div>
    );
  }

  const stats = [
    {
      title: "Total Workouts",
      value: data?.totalWorkouts || 0,
      icon: Dumbbell,
      color: "text-blue-500",
      bg: "bg-blue-500/10",
    },
    {
      title: "Total Groups",
      value: data?.totalGroups || 0,
      icon: FolderOpen,
      color: "text-purple-500",
      bg: "bg-purple-500/10",
    },
    {
      title: "Total Types",
      value: data?.totalTypes || 0,
      icon: Tag,
      color: "text-amber-500",
      bg: "bg-amber-500/10",
    },
    {
      title: "Active Assignments",
      value: data?.totalActiveAssignments || 0,
      icon: Users,
      color: "text-emerald-500",
      bg: "bg-emerald-500/10",
    },
  ];

  return (
    <div className="space-y-6" data-testid="workout-overview-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-black font-heading tracking-tight uppercase">
          Workout Overview
        </h1>
        <p className="text-sm text-muted-foreground">
          Statistics and insights for your workout library
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <Card key={stat.title} className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">
                    {stat.title}
                  </p>
                  <p className="text-3xl font-black font-heading">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-xl ${stat.bg}`}>
                  <stat.icon className={`h-6 w-6 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Additional Insights */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Most Used Group */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-heading uppercase tracking-wide flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              Most Used Group
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data?.mostUsedGroup ? (
              <div className="flex items-center justify-between">
                <span className="text-lg font-semibold">{data.mostUsedGroup.name}</span>
                <span className="text-sm text-muted-foreground">
                  {data.mostUsedGroup.count} workout{data.mostUsedGroup.count !== 1 ? "s" : ""}
                </span>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">No data available</p>
            )}
          </CardContent>
        </Card>

        {/* Most Assigned Workout */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-heading uppercase tracking-wide flex items-center gap-2">
              <Target className="h-4 w-4 text-primary" />
              Most Assigned Workout
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data?.mostAssignedWorkout ? (
              <div className="flex items-center justify-between">
                <span className="text-lg font-semibold">{data.mostAssignedWorkout.title}</span>
                <span className="text-sm text-muted-foreground">
                  {data.mostAssignedWorkout.count} assignment{data.mostAssignedWorkout.count !== 1 ? "s" : ""}
                </span>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">No data available</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
